import React, { useRef } from 'react';
import classNames from 'classnames';
import { useIntersectionObserver } from './useIntersectionObserver';
import { BuyItNowButton } from './BuyItNowButton';
import {
  buildListingForBuyer,
  getA11yPriceDescription,
} from '@reverbdotcom/commons/src/listing_helpers';
import { RCPriceBlock, RCImage } from '@reverbdotcom/cadence/components';
import { Item2StickyBannerDataFragment } from '@reverbdotcom/commons/src/gql/graphql';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface Props {
  listing: Item2StickyBannerDataFragment;
  showBanner?: boolean;
  children: React.ReactNode;
}

export function Item2StickyBanner({
  listing,
  children,
  showBanner = true,
}: Props) {
  const childrenRef = useRef<HTMLDivElement>();
  const bannerRef = useRef<HTMLDivElement>();
  const { entry: childrenEntry } = useIntersectionObserver({ outsideRef: childrenRef });
  const { setTrackedRef: setTrackedRefBottom, entry: bottom } = useIntersectionObserver({});
  const user = useUser();
  const decoratedListing = buildListingForBuyer(listing, user);
  const image = listing.cardSquareImages[0].source;
  const discountPriceDescription = decoratedListing.discountPriceDescription;

  if (!children) return null;

  const entryIsVisible = childrenEntry?.isIntersecting;
  const entryIsVisibleBottom = bottom?.isIntersecting;

  const classes = classNames(
    'item2-sticky-banner',
    { 'item2-sticky-banner--show': entryIsVisible && !entryIsVisibleBottom },
  );

  if (!showBanner) return (<>{children}</>);

  return (
    <div className="item2-sticky-banner__inner" ref={childrenRef}>
      <div className="d-none mobile-d-block">
        {children}
      </div>
      <div className={classes} ref={bannerRef}>
        <div className="site-wrapper">
          <div className="item2-sticky-banner__content">
            <div className="item2-sticky-banner__flex-row item2-sticky-banner__flex-row-title">
              <div className="item2-sticky-banner__image">
                <RCImage
                  src={image}
                  alt={listing.title}
                  key={image}
                  overlay="offwhite"
                  imageBox="auto"
                  borderRadius="lg"
                  eagerLoad
                />
              </div>
              <div className="item2-sticky-banner__text">{listing.title}</div>
            </div>
            <div className="item2-sticky-banner__flex-row item2-sticky-banner__flex-row-price">
              <div>
                <RCPriceBlock
                  priceDisplay={decoratedListing.primaryPriceDisplay}
                  discountPriceDisplay={decoratedListing.discountedPriceDisplay}
                  a11yPriceDescription={getA11yPriceDescription(decoratedListing)}
                  smallText
                />
                {discountPriceDescription &&
                  <div className="item2-sticky-banner__flex-row-price-description">
                    {decoratedListing.discountPriceDescription}
                  </div>
                }
              </div>
              <BuyItNowButton
                listing={listing}
                quantity={1}
                size="large"
              />
            </div>
          </div>
        </div>
      </div>
      <div ref={setTrackedRefBottom} />
      <div className="d-block mobile-d-none">
        {children}
      </div>
    </div>
  );
}

export const Item2StickyBannerFragment = gql(`
  fragment Item2StickyBannerData on Listing {
    _id
    id
    title
    ...ItemImageFields
    ...BuyItNowButtonFields
    ...Item2PricingData
  }
`);
